import { TOrbit } from '../../i18n/TModules';
import { windowErrors } from '../configStudio/configModel';
import { AdapterContext, limitValue, positive } from '../wizard/adapters/common';
import { Translate } from '../wizard/adapters/ruleAdapter';
import { CodeSource, RewardType, RewardWizardState } from './rewardModel';

const TR = `${TOrbit}.configStudio.reward`;
const TE = `${TOrbit}.configStudio.errors`;

const DISPLAY: Partial<Record<RewardType, string>> = {
  text: 'promo_code',
  qr: 'qr_code',
  barcode: 'bar_code',
  url: 'url'
};

// The code display types are read in the wallet, so they redeem offline; which `redemption_type`
// the design means for them is not specified.
const REDEMPTION: Record<RewardType, string> = {
  text: 'offline',
  qr: 'offline',
  barcode: 'offline',
  url: 'online',
  api: 'api_redemption',
  pin: 'pin'
};

const METHOD: Record<CodeSource, string> = {
  fixed: 'single',
  generate: 'multiple',
  csv: 'multiple_with_user_upload',
  inventory: 'inventory'
};

const API_METHOD = {
  none: 'nocode',
  csv: 'multiple_with_user_upload',
  inventory: 'inventory',
  random: 'multiple'
} as const;

const AMOUNT = /^\d+(\.\d{1,2})?$/;
const URL = /^https?:\/\/\S+$/;

/** A reward as the module's `reward_form` holds it (`pages/reward/create.tsx`). */
export type RewardFormValues = Record<string, unknown> & { name_en: string };

export interface RewardWizardValues {
  values: RewardFormValues;
  /** Drawn answers with no module key (backend ask); never sent as reward fields. */
  unsupported: Record<string, unknown>;
}

const codeValues = (state: RewardWizardState): Record<string, unknown> => {
  switch (state.type) {
    case 'url':
      return { code_generation_method: 'nocode', redemption_url: state.url.trim() };
    case 'api':
      return {
        code_generation_method: API_METHOD[state.apiCode],
        ...(state.apiCode === 'inventory' ? { inventory_id: state.inventory?.id ?? null } : {}),
        redemption_data: {
          external_api_integration_id: state.api?.id ?? null,
          // The module's own body-param shape, `None` for an empty part (`RewardMechanics`).
          query_params_values: state.queryParams.map(param => ({
            id: param.id,
            key: param.key,
            value: 'None'
          })),
          body_params_values: state.params.map(param => ({
            id: param.id,
            key: param.key,
            value: param.value.trim() === '' ? 'None' : param.value.trim(),
            prefix: param.prefix.trim() === '' ? 'None' : param.prefix.trim(),
            postfix: param.suffix.trim() === '' ? 'None' : param.suffix.trim()
          }))
        }
      };
    case 'pin':
      return { code_generation_method: 'nocode', merchant_account_id: state.merchant?.id ?? null };
    default:
      return {
        code_generation_method: METHOD[state.source],
        ...(state.source === 'fixed' ? { generic_voucher_code: state.codeValue.trim() } : {}),
        // Without it `rewardCreate` turns `multiple` into the CSV upload method.
        ...(state.source === 'generate' ? { user_generated: true } : {}),
        ...(state.source === 'inventory' ? { inventory_id: state.inventory?.id ?? null } : {})
      };
  }
};

const expiryValues = (state: RewardWizardState, now: string): Record<string, unknown> => {
  if (state.expiry === 'days') {
    return {
      transaction_validity_period_type: 'issuance_date',
      transaction_expires_in_units: Number(state.expiryDays),
      transaction_expires_in_period: 'day'
    };
  }
  if (state.expiry === 'fixed') {
    return {
      transaction_validity_period_type: 'period',
      transaction_issues_at: state.beginsAt ?? now,
      transaction_expires_at: state.expiresAt
    };
  }
  // The module has no "never": its `period` needs both dates (`pages/reward/validation.ts`).
  return { transaction_validity_period_type: null };
};

const interval = (scope: string, maxKey: string, on: boolean, value: string, period: string) =>
  on && positive(value)
    ? {
        [maxKey]: Number(value),
        [`${scope}_interval_units`]: 1,
        [`${scope}_interval_period`]: period
      }
    : { [maxKey]: null };

const unsupportedValues = (state: RewardWizardState): Record<string, unknown> => ({
  ...(state.window.on
    ? {
        availability_window: {
          days: state.window.days,
          from: state.window.from,
          to: state.window.to
        }
      }
    : {}),
  ...(state.expiry === 'never' ? { expiry: 'never' } : {}),
  ...(state.type === 'qr' ? { qr_error_correction: state.ecc } : {}),
  ...(state.type === 'barcode' ? { barcode_symbology: state.symbology } : {}),
  ...(state.type === 'url' && state.urlType === 'unique' ? { url_type: 'unique' } : {})
});

export const buildRewardValues = (
  state: RewardWizardState,
  context: AdapterContext
): RewardWizardValues => {
  const { limits } = state;
  const name = state.name.trim();
  const type = state.type ?? 'text';
  return {
    values: {
      name_en: name,
      reward_publicity_type: state.visibility,
      begins_at: state.beginsAt ?? context.now(),
      ends_at: state.endsOn ? state.endsAt : null,
      redemption_type: REDEMPTION[type],
      ...(DISPLAY[type] !== undefined ? { display_voucher_code_as: DISPLAY[type] } : {}),
      ...codeValues(state),
      ...expiryValues(state, context.now()),
      ...interval(
        'campaign',
        'campaign_interval_max_available',
        limits.issuanceOn,
        limits.issuance,
        limits.issuancePeriod
      ),
      account_lifetime_max_available: limitValue(limits.lifetimeOn, limits.lifetime),
      ...interval(
        'account',
        'account_interval_max_available',
        limits.perUserOn,
        limits.perUser,
        limits.perUserPeriod
      ),
      ...interval(
        'redemption',
        'redemption_interval_max_available',
        limits.redemptionOn,
        limits.redemption,
        limits.redemptionPeriod
      ),
      reward_cost_currency: state.costCurrency,
      reward_cost_amount: AMOUNT.test(state.cost) ? Number(state.cost) : null,
      // One price record per tier (`SellingPrice`); no tier means one record for every tier.
      reward_prices:
        state.sellOn && state.sellLoyalty !== null
          ? (state.sellTiers.length > 0 ? state.sellTiers : [null]).map(tier => ({
              points: Number(state.sellPoints),
              loyalty_program_id: state.sellLoyalty?.id ?? null,
              tier
            }))
          : []
    },
    unsupported: unsupportedValues(state)
  };
};

const sourceErrors = (state: RewardWizardState, t: Translate): string[] => {
  switch (state.type) {
    case 'url':
      return URL.test(state.url.trim()) ? [] : [t(`${TR}.url.required`)];
    case 'api': {
      const errors = state.api === null ? [t(`${TR}.api.required`)] : [];
      if (state.apiCode === 'inventory' && state.inventory === null) {
        errors.push(t(`${TR}.source.inventoryRequired`));
      }
      return errors;
    }
    case 'pin':
      return state.merchant === null ? [t(`${TR}.pin.required`)] : [];
    default:
      if (state.source === 'fixed' && state.codeValue.trim() === '') {
        return [t(`${TR}.source.valueRequired`)];
      }
      if (state.source === 'inventory' && state.inventory === null) {
        return [t(`${TR}.source.inventoryRequired`)];
      }
      return [];
  }
};

const visibilityErrors = (state: RewardWizardState, t: Translate): string[] => {
  const errors: string[] = [];
  if (state.endsOn) {
    if (state.endsAt === null) {
      errors.push(t(`${TR}.visibility.endRequired`));
    } else if (state.beginsAt !== null && state.endsAt <= state.beginsAt) {
      errors.push(t(`${TE}.dateOrder`));
    }
  }
  return [
    ...errors,
    ...windowErrors(state.window, {
      days: t(`${TE}.daysRequired`),
      time: t(`${TE}.required`, { field: t(`${TOrbit}.configStudio.timeWindow`) }),
      order: t(`${TE}.timeOrder`)
    })
  ];
};

const expiryErrors = (state: RewardWizardState, t: Translate): string[] => {
  if (state.expiry === 'fixed' && state.expiresAt === null) {
    return [t(`${TR}.expiry.fixedRequired`)];
  }
  if (state.expiry === 'days' && !positive(state.expiryDays)) {
    return [t(`${TR}.expiry.daysRequired`)];
  }
  return [];
};

const limitErrors = (state: RewardWizardState, t: Translate): string[] => {
  const { limits } = state;
  const L = `${TR}.limits`;
  const checked: [boolean, string, string][] = [
    [limits.issuanceOn, limits.issuance, `${L}.issuance`],
    [limits.lifetimeOn, limits.lifetime, `${L}.lifetime`],
    [limits.perUserOn, limits.perUser, `${L}.perUser`],
    [limits.redemptionOn, limits.redemption, `${L}.redemption`],
    [state.sellOn, state.sellPoints, `${L}.sell`]
  ];
  const errors = checked
    .filter(([on, value]) => on && !positive(value))
    .map(([, , key]) => t(`${TE}.positive`, { field: t(key) }));
  if (state.cost !== '' && !AMOUNT.test(state.cost)) {
    errors.unshift(t(`${L}.costInvalid`));
  }
  if (state.sellOn && state.sellLoyalty === null) {
    errors.push(t(`${L}.loyaltyRequired`));
  }
  return errors;
};

/** What still blocks Next on the current step; the type pick never blocks. */
export const rewardStepErrors = (state: RewardWizardState, t: Translate): string[] => {
  switch (state.step) {
    case 'source':
      return sourceErrors(state, t);
    case 'visibility':
      return visibilityErrors(state, t);
    case 'expiry':
      return expiryErrors(state, t);
    case 'limits':
      return limitErrors(state, t);
    default:
      return [];
  }
};
